(() => {
  'use strict'

  const MC = (window.MembracleanTheme = window.MembracleanTheme || {})
  MC.name = 'membraclean'
  MC.version = '1.0.0'
  MC.ready = false
  MC.routes = { root: window.Shopify?.routes?.root || '/' }
  MC.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }))

  class McIsland extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mcIslandReady === 'true') return
      this.dataset.mcIslandReady = 'true'

      const template = this.querySelector(':scope > template[data-island]')
      if (!template) return

      const hydrate = () => {
        if (!template.isConnected) return
        template.replaceWith(template.content.cloneNode(true))
        this.dataset.mcIslandHydrated = 'true'
      }

      if (this.hasAttribute('on:visible') && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return
          observer.disconnect()
          hydrate()
        }, { rootMargin: '200px' })
        observer.observe(this)
        return
      }

      if (this.hasAttribute('on:idle') && 'requestIdleCallback' in window) {
        window.requestIdleCallback(hydrate, { timeout: 1500 })
        return
      }

      hydrate()
    }
  }

  if (!customElements.get('is-land')) customElements.define('is-land', McIsland)

  const enhanceShippingPolicy = () => {
    if (!location.pathname.includes('/policies/shipping-policy')) return

    const policy = document.querySelector('.shopify-policy__body')
    if (!policy) return

    // The visible hero contains the page H1; remove Shopify's hidden H1
    // to avoid duplicate heading hierarchy in the document.
    policy.closest('.shopify-policy__container')?.querySelector('.shopify-policy__title')?.remove()

    // Shopify may output a policy's embedded CSS as visible text.
    // The styles are maintained in mc-theme.css instead.
    const walker = document.createTreeWalker(policy, NodeFilter.SHOW_TEXT)
    const exposedStyles = []
    while (walker.nextNode()) {
      const node = walker.currentNode
      const value = node.nodeValue || ''
      if (value.includes('.mc-shipping .mc-shipping__tracking-grid') &&
          value.includes('grid-template-columns')) {
        exposedStyles.push(node)
      }
    }
    exposedStyles.forEach(node => { node.nodeValue = '' })

    // Shopify policy sanitization can remove <section> elements while keeping
    // their children. Recreate the cards required by the original page design.
    const restoreCards = (selector) => {
      policy.querySelectorAll(selector).forEach(grid => {
        if (grid.querySelector(':scope > .mc-shipping__card')) return

        const headings = Array.from(grid.children).filter(el => el.matches('h3'))
        if (headings.length < 2) return

        let card = null
        Array.from(grid.childNodes).forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE && node.matches('h3')) {
            card = document.createElement('div')
            card.className = 'mc-shipping__card'
            grid.insertBefore(card, node)
          }
          if (card) card.appendChild(node)
        })
      })
    }

    restoreCards('.mc-shipping__rates')
    restoreCards('.mc-shipping__info-grid')

    // Restore the bordered country panel around heading, text and country grid.
    policy.querySelectorAll('.mc-shipping__country-grid').forEach(grid => {
      if (grid.closest('.mc-shipping__country-panel')) return
      const description = grid.previousElementSibling
      const heading = description?.previousElementSibling
      if (!heading?.matches('h2') || !description?.matches('p')) return
      const panel = document.createElement('div')
      panel.className = 'mc-shipping__country-panel'
      heading.parentNode.insertBefore(panel, heading)
      panel.append(heading, description, grid)
    })

    // Put the Packstation description and the existing unmodified DHL image
    // back into the two-column panel from the original shipping page.
    policy.querySelectorAll('img[src*="dhl-ps.jpg"]').forEach(image => {
      if (image.closest('.mc-shipping__packstation')) return
      const visual = image.closest('figure') || image
      const content = visual.previousElementSibling
      if (!content?.matches('div') || !content.querySelector('h2')) return
      if (content.parentElement !== visual.parentElement) return
      const panel = document.createElement('div')
      panel.className = 'mc-shipping__packstation'
      content.parentNode.insertBefore(panel, content)
      panel.append(content, visual)
    })
  }

  const initFrenchReviewTab = () => {
    if (MC.frenchReviewTabReady) return
    let locale = window.Shopify?.locale || ''
    const config = document.getElementById('mc-auto-language-config')
    if (config) {
      try {
        locale = JSON.parse(config.textContent).current || locale
      } catch (_) {}
    }
    if (!locale && /^\/fr(?:\/|$)/.test(location.pathname)) locale = 'fr'
    if (String(locale).toLowerCase().split('-')[0] !== 'fr') return
    MC.frenchReviewTabReady = true

    const selector = '.jdgm-revs-tab-btn'
    const relabel = (tab) => {
      const walker = document.createTreeWalker(tab, NodeFilter.SHOW_TEXT)
      while (walker.nextNode()) {
        const node = walker.currentNode
        const before = node.nodeValue || ''
        const after = before.replace(/\bAvis\b/g, 'Évaluations')
        if (after !== before) node.nodeValue = after
      }
      for (const key of ['aria-label', 'title']) {
        const before = tab.getAttribute(key)
        if (!before) continue
        const after = before.replace(/\bAvis\b/g, 'Évaluations')
        if (after !== before) tab.setAttribute(key, after)
      }
    }
    const containingTab = (node) => {
      const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
      return element?.closest(selector)
    }
    const visit = (node) => {
      const tab = containingTab(node)
      if (tab) relabel(tab)
      if (node.nodeType === Node.ELEMENT_NODE) node.querySelectorAll(selector).forEach(relabel)
    }
    visit(document.body)
    new MutationObserver((records) => {
      for (const record of records) {
        const tab = containingTab(record.target)
        if (tab) relabel(tab)
        if (record.type === 'childList') record.addedNodes.forEach(visit)
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-label', 'title']
    })
  }

  const boot = () => {
    enhanceShippingPolicy()
    if (MC.ready) return
    MC.ready = true
    initFrenchReviewTab()
    document.documentElement.dataset.mcThemeReady = 'true'
    MC.emit('mc:ready', { name: MC.name, version: MC.version })
    MC.emit('page:loaded', { source: 'membraclean', version: MC.version })
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true })
  else boot()

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) MC.emit('mc:pageshow', { persisted: true })
  })

  document.addEventListener('shopify:section:load', (event) => {
    if (!window.Shopify?.designMode) return
    MC.emit('mc:section:load', event.detail || {})
  })

  document.addEventListener('shopify:section:unload', (event) => {
    if (!window.Shopify?.designMode) return
    MC.emit('mc:section:unload', event.detail || {})
  })
})()
